import React from "react";
import {
    List,
    TopToolbar,
    useListContext,
} from "react-admin";
import Button from "@mui/material/Button";
import DownloadIcon from "@mui/icons-material/Download";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

const formatMoney = (value: string | null | undefined): string => {
    if (value === null || value === undefined) return "";
    const num = parseFloat(value);
    return num.toLocaleString("en-GB", { style: "currency", currency: "GBP" });
};

const formatDate = (dateStr: string): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB");
};

const ContractualArrearsV2Actions = () => {
    const { data } = useListContext();

    const handleExport = () => {
        if (!data || data.length === 0) return;

        const headers = [
            "Date",
            "Contracted Amount",
            "Received Amount",
            "Cumulative Contracted",
            "Cumulative Received",
            "Arrears",
            "Payments Down",
            "P&I Balance",
        ];

        const rows = data.map((row: any) => [
            row.date,
            row.contracted_amount,
            row.received_amount,
            row.cumulative_contracted,
            row.cumulative_received,
            row.arrears,
            row.payments_down ?? "",
            row.principal_and_interest_balance || "",
        ]);

        const csvContent = [
            headers.join(","),
            ...rows.map((row: string[]) => row.join(",")),
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `contractual_arrears_v2_${new Date().toISOString().split("T")[0]}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <TopToolbar>
            <Button
                onClick={handleExport}
                startIcon={<DownloadIcon />}
                size="small"
                disabled={!data || data.length === 0}
            >
                Export CSV
            </Button>
        </TopToolbar>
    );
};

const ContractualArrearsV2Table = () => {
    const { data } = useListContext();

    if (!data) return null;

    return (
        <TableContainer sx={{ maxHeight: "calc(100vh - 250px)" }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Contracted Amount</TableCell>
                        <TableCell align="right">Received Amount</TableCell>
                        <TableCell align="right">Cumulative Contracted</TableCell>
                        <TableCell align="right">Cumulative Received</TableCell>
                        <TableCell align="right">Arrears</TableCell>
                        <TableCell align="right">Payments Down</TableCell>
                        <TableCell align="right">P&I Balance</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((row: any, index: number) => {
                        const arrears = parseFloat(row.arrears || "0");
                        const isInArrears = arrears > 0;
                        const isAhead = arrears < 0;

                        return (
                            <TableRow
                                key={row.date}
                                sx={{
                                    backgroundColor: index % 2 === 0 ? "#fff" : "#f9f9f9",
                                    ...(isInArrears && {
                                        backgroundColor: index % 2 === 0 ? "#ffebee" : "#ffcdd2",
                                    }),
                                    ...(isAhead && {
                                        backgroundColor: index % 2 === 0 ? "#e8f5e9" : "#c8e6c9",
                                    }),
                                }}
                            >
                                <TableCell>{formatDate(row.date)}</TableCell>
                                <TableCell align="right">{formatMoney(row.contracted_amount)}</TableCell>
                                <TableCell align="right">{formatMoney(row.received_amount)}</TableCell>
                                <TableCell align="right">{formatMoney(row.cumulative_contracted)}</TableCell>
                                <TableCell align="right">{formatMoney(row.cumulative_received)}</TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: isInArrears || isAhead ? "bold" : "normal",
                                        color: isInArrears ? "#c62828" : isAhead ? "#2e7d32" : "inherit",
                                    }}
                                >
                                    {formatMoney(row.arrears)}
                                </TableCell>
                                <TableCell align="right" sx={{ color: "#000" }}>
                                    {row.payments_down != null ? Number(row.payments_down).toFixed(2) : "-"}
                                </TableCell>
                                <TableCell align="right">
                                    {row.principal_and_interest_balance
                                        ? formatMoney(row.principal_and_interest_balance)
                                        : "-"}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const ContractualArrearsV2Entries = ({ externalId }: { externalId: string | number }) => {
    return (
        <List
            pagination={false}
            perPage={500}
            actions={<ContractualArrearsV2Actions />}
        >
            <ContractualArrearsV2Table />
        </List>
    );
};

const ContractualArrearsV2 = { list: ContractualArrearsV2Entries };

export default ContractualArrearsV2;
